import type { IconName } from '@/shared/components/ui/Icon';
import type { MessageKey } from '@/shared/i18n/messages';
import type { Translate } from '@/shared/i18n/I18nProvider';
import type { FoldersDocType, SheetColumn } from './types';

/**
 * The templates a user can create.
 *
 * This file is where "every file is a Sheet" actually lives. A template is a
 * label, an icon and a set of starting columns — nothing else. Adding one is an
 * entry here; it costs no migration, because `folders_nodes.doc_type` is a free
 * string constrained only by a regex.
 *
 * Column names are **content**, not interface: they are written into
 * `sheet_documents.columns` in the language the creator was using and stay that
 * way afterwards, exactly as `supabase/AGENTS.md` requires of user-generated
 * text. `t` is passed in for that one moment of creation and never consulted
 * again — renaming the column later is the user's, not a translation's.
 */
export interface FoldersTemplate {
  readonly docType: FoldersDocType;
  readonly icon: IconName;
  readonly nameKey: MessageKey;
  /** Which module's limits and permissions the document answers to. */
  readonly moduleKey: string;
  readonly initialRows: number;
  readonly defaultName: (t: Translate) => string;
  readonly defaultTabName: (t: Translate) => string;
  readonly columns: (t: Translate) => SheetColumn[];
  /** Additional blank tabs created with the document, when a template needs a multi-sheet shape. */
  readonly additionalTabs?: (t: Translate) => readonly FoldersTemplateTab[];
}

export interface FoldersTemplateTab {
  readonly name: string;
  readonly columns: readonly SheetColumn[];
}

/** The columns an income and an expense share, after the description. */
const cashflowFlowColumns = (t: Translate): SheetColumn[] => [
  { id: 'accountId', name: t('cashflow.fields.accountId'), cellType: 'text' },
  { id: 'amount', name: t('cashflow.fields.amount'), cellType: 'number' },
  { id: 'date', name: t('cashflow.fields.date'), cellType: 'date' },
  { id: 'personId', name: t('cashflow.fields.personId'), cellType: 'text' },
  { id: 'recurrence', name: t('cashflow.fields.recurrence'), cellType: 'text' },
  { id: 'endDate', name: t('cashflow.fields.endDate'), cellType: 'date' },
  { id: 'paidDate', name: t('cashflow.fields.paidDate'), cellType: 'date' },
  { id: 'forecastRule', name: t('cashflow.fields.forecastRule'), cellType: 'text' },
  { id: 'minimumUplift', name: t('cashflow.fields.minimumUplift'), cellType: 'number' },
];

export const TEMPLATES: Readonly<Record<FoldersDocType, FoldersTemplate>> = {
  cashflow: {
    docType: 'cashflow', icon: 'cashflow', nameKey: 'folders.type.cashflow',
    moduleKey: 'cashflow', initialRows: 0,
    defaultName: (t) => t('folders.defaultName.cashflow'),
    defaultTabName: (t) => t('cashflow.sheetTabs.incomes'),
    // One tab per kind of record, each recognized by its first column id; nested
    // records point at their parent row (see modules/cashflow/rows.ts).
    columns: (t) => [
      { id: 'income', name: t('cashflow.fields.description'), cellType: 'text', width: 200 },
      ...cashflowFlowColumns(t),
      { id: 'hours', name: t('cashflow.fields.hours'), cellType: 'number' },
      { id: 'increase', name: t('cashflow.fields.increase'), cellType: 'number' },
      { id: 'emailDate', name: t('cashflow.fields.emailDate'), cellType: 'date' },
    ],
    additionalTabs: (t) => [
      {
        name: t('cashflow.sheetTabs.expenses'),
        columns: [
          { id: 'expense', name: t('cashflow.fields.description'), cellType: 'text', width: 200 },
          ...cashflowFlowColumns(t),
          { id: 'categoryId', name: t('cashflow.fields.categoryId'), cellType: 'text' },
          { id: 'paymentMethodId', name: t('cashflow.fields.paymentMethodId'), cellType: 'text' },
          { id: 'statementId', name: t('cashflow.fields.statementId'), cellType: 'text' },
          { id: 'isStatement', name: t('cashflow.fields.isStatement'), cellType: 'checkbox' },
        ],
      },
      {
        name: t('cashflow.sheetTabs.accounts'),
        columns: [
          { id: 'account', name: t('cashflow.fields.name'), cellType: 'text', width: 180 },
          { id: 'personId', name: t('cashflow.fields.personId'), cellType: 'text' },
          { id: 'accountCurrency', name: t('cashflow.fields.currency'), cellType: 'text', width: 90 },
          { id: 'accountKind', name: t('cashflow.fields.kind'), cellType: 'text', width: 110 },
          { id: 'snapshotDate', name: t('cashflow.fields.snapshotDate'), cellType: 'date' },
          { id: 'symbol', name: t('cashflow.fields.symbol'), cellType: 'text' },
          { id: 'quantity', name: t('cashflow.fields.quantity'), cellType: 'number' },
          { id: 'manualPrice', name: t('cashflow.fields.manualPrice'), cellType: 'number' },
          { id: 'quoteUnitSize', name: t('cashflow.fields.quoteUnitSize'), cellType: 'number' },
        ],
      },
      {
        name: t('cashflow.sheetTabs.snapshots'),
        columns: [
          { id: 'snapshotAccountId', name: t('cashflow.fields.accountId'), cellType: 'text' },
          { id: 'month', name: t('cashflow.fields.month'), cellType: 'text', width: 90 },
          { id: 'balance', name: t('cashflow.fields.actual'), cellType: 'number' },
        ],
      },
      {
        name: t('cashflow.sheetTabs.occurrences'),
        columns: [
          { id: 'overrideFlowId', name: t('cashflow.fields.parentRow'), cellType: 'text' },
          { id: 'occurrenceDate', name: t('cashflow.fields.occurrenceDate'), cellType: 'date' },
          { id: 'overrideAmount', name: t('cashflow.fields.overrideAmount'), cellType: 'number' },
          { id: 'overrideDate', name: t('cashflow.fields.overrideDate'), cellType: 'date' },
          { id: 'overridePaidDate', name: t('cashflow.fields.overridePaidDate'), cellType: 'date' },
          { id: 'skipped', name: t('cashflow.fields.skipped'), cellType: 'checkbox' },
        ],
      },
      {
        name: t('cashflow.sheetTabs.history'),
        columns: [
          { id: 'historyFlowId', name: t('cashflow.fields.parentRow'), cellType: 'text' },
          { id: 'historyAmount', name: t('cashflow.fields.amount'), cellType: 'number' },
        ],
      },
      {
        name: t('cashflow.sheetTabs.people'),
        columns: [
          { id: 'person', name: t('cashflow.fields.name'), cellType: 'text', width: 180 },
          { id: 'reserve', name: t('cashflow.fields.reserve'), cellType: 'number' },
        ],
      },
      {
        name: t('cashflow.sheetTabs.categories'),
        columns: [{ id: 'categoryName', name: t('cashflow.fields.name'), cellType: 'text', width: 180 }],
      },
      {
        name: t('cashflow.sheetTabs.paymentMethods'),
        columns: [
          { id: 'paymentMethod', name: t('cashflow.fields.name'), cellType: 'text', width: 180 },
          { id: 'accountId', name: t('cashflow.fields.accountId'), cellType: 'text' },
          { id: 'methodKind', name: t('cashflow.fields.kind'), cellType: 'text' },
          { id: 'statementDay', name: t('cashflow.fields.statementDay'), cellType: 'number' },
        ],
      },
      {
        name: t('cashflow.sheetTabs.reminders'),
        columns: [
          { id: 'reminder', name: t('cashflow.fields.description'), cellType: 'text', width: 200 },
          { id: 'date', name: t('cashflow.fields.date'), cellType: 'date' },
          { id: 'personId', name: t('cashflow.fields.personId'), cellType: 'text' },
        ],
      },
    ],
  },
  sheet: {
    docType: 'sheet',
    icon: 'sheets',
    nameKey: 'folders.type.sheet',
    moduleKey: 'sheets',
    initialRows: 10,
    defaultName: (t) => t('folders.defaultName.sheet'),
    defaultTabName: (t) => t('folders.defaultTabName.sheet'),
    // Four empty columns and 10 rows rather than none: a grid with no cells has
    // nowhere to type, and "add a row/column first" is a bad first screen.
    columns: (t) => [
      { id: 'c1', name: t('sheet.newColumn', { n: 1 }), cellType: 'text', dataFormat: { type: 'auto' } },
      { id: 'c2', name: t('sheet.newColumn', { n: 2 }), cellType: 'text', dataFormat: { type: 'auto' } },
      { id: 'c3', name: t('sheet.newColumn', { n: 3 }), cellType: 'text', dataFormat: { type: 'auto' } },
      { id: 'c4', name: t('sheet.newColumn', { n: 4 }), cellType: 'text', dataFormat: { type: 'auto' } },
    ],
  },
  // A note is a Sheet whose first tab is a text sheet: a document to write in, which can
  // still grow table tabs beside it.
  note: {
    docType: 'note',
    icon: 'notes',
    nameKey: 'folders.type.note',
    moduleKey: 'sheets',
    initialRows: 1,
    defaultName: (t) => t('folders.defaultName.note'),
    defaultTabName: (t) => t('folders.defaultTabName.note'),
    columns: () => [{ id: '__content__', name: 'Document', cellType: 'text_document' }],
  },
  form: {
    docType: 'form',
    icon: 'sheets',
    nameKey: 'folders.type.form',
    moduleKey: 'forms',
    initialRows: 0,
    defaultName: (t) => t('folders.defaultName.form'),
    defaultTabName: (t) => t('folders.defaultTabName.form'),
    // Each column is a question; the form view picks which ones it asks.
    columns: (t) => [
      { id: 'name', name: t('folders.columns.form.name'), cellType: 'text', width: 180 },
      { id: 'email', name: t('folders.columns.form.email'), cellType: 'text', width: 200 },
      { id: 'message', name: t('folders.columns.form.message'), cellType: 'text', width: 320, multiline: true },
    ],
  },
  investments: {
    docType: 'investments',
    icon: 'investments',
    nameKey: 'folders.type.investments',
    moduleKey: 'sheets',
    initialRows: 10,
    defaultName: (t) => t('folders.defaultName.investments'),
    defaultTabName: (t) => t('folders.defaultTabName.investments'),
    // Price and value are not stored: the module computes them from the ticker's quote
    // and the organization's exchange rates, and appends them beside these.
    columns: (t) => [
      { id: 'broker', name: t('investments.columns.broker'), cellType: 'text', width: 110 },
      { id: 'ticker', name: t('investments.columns.asset'), cellType: 'text', width: 110 },
      { id: 'name', name: t('investments.columns.name'), cellType: 'text', width: 160 },
      { id: 'category', name: t('investments.columns.category'), cellType: 'text', width: 130 },
      { id: 'quantity', name: t('investments.columns.quantity'), cellType: 'number', width: 105 },
      { id: 'amount', name: t('investments.columns.amount'), cellType: 'number', dataFormat: { type: 'number', formatKey: 'currency' }, width: 120 },
    ],
  },
  calendar: {
    docType: 'calendar',
    icon: 'calendar',
    nameKey: 'folders.type.calendar',
    moduleKey: 'sheets',
    initialRows: 10,
    defaultName: (t) => t('folders.defaultName.calendar'),
    defaultTabName: (t) => t('folders.defaultTabName.calendar'),
    columns: (t) => [
      { id: 'date', name: t('folders.columns.calendar.date'), cellType: 'date' },
      { id: 'time', name: t('folders.columns.calendar.time'), cellType: 'text' },
      { id: 'client', name: t('folders.columns.calendar.client'), cellType: 'text' },
      { id: 'service', name: t('folders.columns.calendar.service'), cellType: 'text' },
      { id: 'status', name: t('folders.columns.calendar.status'), cellType: 'chip' },
    ],
  },
  trip: {
    docType: 'trip',
    icon: 'calendar',
    nameKey: 'folders.type.trip',
    moduleKey: 'sheets',
    initialRows: 0,
    defaultName: (t) => t('folders.defaultName.trip'),
    defaultTabName: (t) => t('folders.defaultTabName.trip'),
    columns: (t) => [
      { id: 'destination', name: t('folders.columns.trip.destination'), cellType: 'text', width: 110 },
      { id: 'nights', name: t('folders.columns.trip.nights'), cellType: 'number', dataFormat: { type: 'number', formatKey: 'integer' }, width: 80 },
      { id: 'checkIn', name: t('folders.columns.trip.checkIn'), cellType: 'date', dataFormat: { type: 'date', formatKey: 'date' }, width: 105 },
      { id: 'checkOut', name: t('folders.columns.trip.checkOut'), cellType: 'date', dataFormat: { type: 'date', formatKey: 'date' }, width: 105 },
      { id: 'totalUsd', name: t('folders.columns.trip.totalUsd'), cellType: 'number', dataFormat: { type: 'number', formatKey: 'currency' }, width: 120 },
      { id: 'preselected', name: t('folders.columns.trip.preselected'), cellType: 'checkbox', width: 110 },
      { id: 'confirmed', name: t('folders.columns.trip.confirmed'), cellType: 'checkbox', width: 100 },
      { id: 'lodging', name: t('folders.columns.trip.lodging'), cellType: 'text', width: 220 },
      { id: 'instagram', name: t('folders.columns.trip.instagram'), cellType: 'text', width: 150 },
      { id: 'rating', name: t('folders.columns.trip.rating'), cellType: 'number', dataFormat: { type: 'number', formatKey: 'integer' }, width: 95 },
      { id: 'reviewCount', name: t('folders.columns.trip.reviewCount'), cellType: 'number', dataFormat: { type: 'number', formatKey: 'integer' }, width: 95 },
      { id: 'heating', name: t('folders.columns.trip.heating'), cellType: 'text', width: 180 },
      { id: 'notes', name: t('folders.columns.trip.notes'), cellType: 'text', width: 280, multiline: true },
      { id: 'platform', name: t('folders.columns.trip.platform'), cellType: 'text', width: 130 },
    ],
    additionalTabs: (t) => [
      {
        name: t('trip.tabs.itinerary'),
        columns: [
          { id: 'day', name: t('trip.tabs.columns.day'), cellType: 'number' },
          { id: 'date', name: t('trip.tabs.columns.date'), cellType: 'date' },
          { id: 'nightIn', name: t('trip.tabs.columns.nightIn'), cellType: 'text' },
          { id: 'lodging', name: t('trip.tabs.columns.lodging'), cellType: 'text' },
          { id: 'prices', name: t('trip.tabs.columns.prices'), cellType: 'text' },
          { id: 'time', name: t('trip.tabs.columns.time'), cellType: 'text' },
          { id: 'route', name: t('trip.tabs.columns.route'), cellType: 'text' },
          { id: 'places', name: t('trip.tabs.columns.places'), cellType: 'text' },
        ],
      },
      {
        name: t('trip.tabs.foodie'),
        columns: [
          { id: 'destination', name: t('trip.tabs.columns.destination'), cellType: 'text' },
          { id: 'place', name: t('trip.tabs.columns.place'), cellType: 'text' },
          { id: 'link', name: t('trip.tabs.columns.link'), cellType: 'smartchip' },
        ],
      },
      {
        name: t('trip.tabs.codes'),
        columns: [
          { id: 'category', name: t('trip.tabs.columns.category'), cellType: 'text' },
          { id: 'toyti', name: t('trip.tabs.columns.toyti'), cellType: 'text' },
          { id: 'juan', name: t('trip.tabs.columns.juan'), cellType: 'text' },
        ],
      },
      {
        name: t('trip.tabs.flights'),
        columns: [
          { id: 'leg', name: t('trip.tabs.columns.leg'), cellType: 'text' },
          { id: 'date', name: t('trip.tabs.columns.date'), cellType: 'date' },
          { id: 'origin', name: t('trip.tabs.columns.origin'), cellType: 'text' },
          { id: 'destination', name: t('trip.tabs.columns.destination'), cellType: 'text' },
          { id: 'departure', name: t('trip.tabs.columns.departure'), cellType: 'text' },
          { id: 'arrival', name: t('trip.tabs.columns.arrival'), cellType: 'text' },
          { id: 'duration', name: t('trip.tabs.columns.duration'), cellType: 'text' },
          { id: 'airline', name: t('trip.tabs.columns.airline'), cellType: 'text' },
          { id: 'stops', name: t('trip.tabs.columns.stops'), cellType: 'text' },
          { id: 'passengers', name: t('trip.tabs.columns.passengers'), cellType: 'number' },
          { id: 'fareArs', name: t('trip.tabs.columns.fareArs'), cellType: 'number' },
          { id: 'taxesArs', name: t('trip.tabs.columns.taxesArs'), cellType: 'number' },
          { id: 'chargesArs', name: t('trip.tabs.columns.chargesArs'), cellType: 'number' },
          { id: 'totalArs', name: t('trip.tabs.columns.totalArs'), cellType: 'number' },
          { id: 'summary', name: t('trip.tabs.columns.summary'), cellType: 'text' },
        ],
      },
    ],
  },
  // Trip Board stores no grid columns of its own — its document envelope is
  // the generic folders_nodes/sheet_documents/sheet_tabs/sheet_rows tables,
  // but the shape inside them is a nested destinations->categories->items
  // structure (src/modules/trips/board/types.ts), not spreadsheet cells.
  // `sheet_documents.columns` is repurposed to hold display settings instead
  // — see src/modules/trips/board/queries.ts.
  trip_board: {
    docType: 'trip_board',
    icon: 'plane',
    nameKey: 'folders.type.trip_board',
    moduleKey: 'sheets',
    initialRows: 0,
    defaultName: (t) => t('folders.defaultName.trip_board'),
    defaultTabName: (t) => t('folders.defaultTabName.trip_board'),
    // One tab per level of the board, each recognized by its first column id; children
    // point at their parent row through a reference column (see trips/board/rows.ts).
    columns: (t) => [
      { id: 'destination', name: t('folders.columns.tripBoard.destination'), cellType: 'text', width: 180 },
      { id: 'startDate', name: t('folders.columns.tripBoard.startDate'), cellType: 'date', dataFormat: { type: 'date', formatKey: 'date' }, width: 110 },
      { id: 'endDate', name: t('folders.columns.tripBoard.endDate'), cellType: 'date', dataFormat: { type: 'date', formatKey: 'date' }, width: 110 },
      { id: 'transportMode', name: t('folders.columns.tripBoard.transportMode'), cellType: 'text', width: 130 },
      { id: 'transportNotes', name: t('folders.columns.tripBoard.transportNotes'), cellType: 'text', width: 220 },
    ],
    additionalTabs: (t) => [
      {
        name: t('trip.board.tabs.items'),
        columns: [
          { id: 'item', name: t('folders.columns.tripBoard.item'), cellType: 'text', width: 180 },
          { id: 'destinationId', name: t('folders.columns.tripBoard.destinationId'), cellType: 'text', width: 120 },
          { id: 'itemCategory', name: t('folders.columns.tripBoard.category'), cellType: 'text', width: 110 },
          { id: 'status', name: t('folders.columns.tripBoard.status'), cellType: 'text', width: 110 },
          { id: 'rating', name: t('folders.columns.tripBoard.rating'), cellType: 'number', width: 90 },
          { id: 'lodgingType', name: t('folders.columns.tripBoard.lodgingType'), cellType: 'text', width: 130 },
          { id: 'commentCount', name: t('folders.columns.tripBoard.commentCount'), cellType: 'number', dataFormat: { type: 'number', formatKey: 'integer' }, width: 100 },
          { id: 'address', name: t('folders.columns.tripBoard.address'), cellType: 'text', width: 200 },
          { id: 'email', name: t('folders.columns.tripBoard.email'), cellType: 'text', width: 160 },
          { id: 'phone', name: t('folders.columns.tripBoard.phone'), cellType: 'text', width: 130 },
          { id: 'whatsapp', name: t('folders.columns.tripBoard.whatsapp'), cellType: 'text', width: 130 },
          { id: 'notes', name: t('folders.columns.tripBoard.notes'), cellType: 'text', width: 260, multiline: true },
        ],
      },
      {
        name: t('trip.board.tabs.costs'),
        columns: [
          { id: 'costItemId', name: t('folders.columns.tripBoard.itemId'), cellType: 'text', width: 120 },
          { id: 'costCurrency', name: t('folders.columns.tripBoard.currency'), cellType: 'text', width: 90 },
          { id: 'priceType', name: t('folders.columns.tripBoard.priceType'), cellType: 'text', width: 110 },
          { id: 'netAmount', name: t('folders.columns.tripBoard.netAmount'), cellType: 'number', width: 120 },
          { id: 'rateMode', name: t('folders.columns.tripBoard.rateMode'), cellType: 'text', width: 110 },
          { id: 'manualRate', name: t('folders.columns.tripBoard.manualRate'), cellType: 'number', width: 110 },
        ],
      },
      {
        name: t('trip.board.tabs.platforms'),
        columns: [
          { id: 'platformItemId', name: t('folders.columns.tripBoard.itemId'), cellType: 'text', width: 120 },
          { id: 'url', name: t('folders.columns.tripBoard.url'), cellType: 'text', width: 220 },
          { id: 'platform', name: t('folders.columns.tripBoard.platform'), cellType: 'text', width: 120 },
          { id: 'platformRating', name: t('folders.columns.tripBoard.rating'), cellType: 'number', width: 90 },
          { id: 'platformComments', name: t('folders.columns.tripBoard.comments'), cellType: 'text', width: 240, multiline: true },
        ],
      },
      {
        name: t('trip.board.tabs.fields'),
        columns: [
          { id: 'fieldItemId', name: t('folders.columns.tripBoard.itemId'), cellType: 'text', width: 120 },
          { id: 'fieldName', name: t('folders.columns.tripBoard.fieldName'), cellType: 'text', width: 150 },
          { id: 'fieldType', name: t('folders.columns.tripBoard.fieldType'), cellType: 'text', width: 100 },
          { id: 'fieldValue', name: t('folders.columns.tripBoard.fieldValue'), cellType: 'text', width: 200 },
        ],
      },
    ],
  },
};

export const TEMPLATE_ORDER: readonly FoldersDocType[] = ['sheet', 'note', 'investments', 'cashflow', 'trip', 'trip_board', 'form', 'calendar'];

export function templateFor(docType: string | null): FoldersTemplate | null {
  if (!docType) return null;
  return TEMPLATES[docType as FoldersDocType] ?? null;
}

/**
 * Where a folder opens: Home's My files tab, at an address that can be
 * bookmarked or sent. The root is `null`.
 */
export function folderPath(id: string | null): string {
  return id ? `/folders/${encodeURIComponent(id)}` : '/folders';
}

/**
 * Where a document opens. Every template is a Sheet today, so they all land in
 * the same editor; when `calendar` grows its own view this is the one place
 * that has to know.
 */
export function editorPathFor(id: string, docType: string | null = null): string {
  switch (docType) {
    case 'cashflow': return `/cashflow/${id}`;
    case 'trip': return `/trips/${id}`;
    // A distinct path segment, not a ?view= flag on /trips/:id: TopBar and
    // AppShell derive their document chrome from the URL alone, synchronously,
    // before the node's docType has loaded — see src/shared/components/TopBar.tsx.
    case 'trip_board': return `/trips/board/${id}`;
    case 'investments': return `/investments/${id}`;
    case 'note': return `/notes/${id}`;
    case 'form': return `/forms/${id}`;
    case 'calendar': return `/calendar/${id}`;
    default: return `/sheets/${id}`;
  }
}




